import { CancelToken } from 'axios';
import { TrackJS } from 'trackjs';

import request from '@module/http/axios/request';

import Config from './config';
import Gauge from './gauge';
import API from './api';

export default class Job {
  constructor(transfer, file, chunk) {
    this.transfer = transfer;
    this.chunk = chunk;
    this.file = file;

    this.offset = 0;
    this.length = 0;
    this.crc = 0;
    this.attempts = 0;
    this.buffer = false;
    this.state = 'init';
    this.cancelToken = null;
    this.times = {
      start: 0,
      upload: 0,
    };
  }

  abort() {
    this.state = 'stopped';
    if (this.cancelToken) {
      this.cancelToken.cancel();
    }

    this.file.resetChunk(this.chunk); // reset this chunk to try again later
  }

  shouldContinue() {
    // If the job state is `stopped` reset the chunk
    // and throw an exception. Yeah baby.
    if (this.state !== 'stopped') {
      return;
    }

    throw 'JobStopped';
  }

  async start() {
    this.state = 'started';

    // Measure how long this job takes
    this.times.start = Gauge.start();

    // Make sure there is a filename
    await this.validateId();
    this.shouldContinue();

    // Verify the filename
    this.validateFile();
    this.shouldContinue();

    // Calculate the offset and length
    this.calculateOffset();
    this.shouldContinue();

    // Read the chunk into buffer
    this.readChunk();
    this.shouldContinue();

    // Attempt to upload the chunk
    let chunkFailed = true;
    while (this.attempts < Config.upload.retry.limit) {
      const response = await this.requestForm();

      try {
        this.shouldContinue();
        await this.putChunk(response.url);
        this.shouldContinue();

        // it worked, stop this loop.
        chunkFailed = false;
        break;
      } catch (error) {
        TrackJS.track(error);

        // Count this attempt, sleep for interval
        this.attempts++;

        // Still in limit? Wait before retrying.
        if (this.attempts < Config.upload.retry.limit) {
          await this.sleep(Config.upload.retry.interval);
          this.shouldContinue();
        } else {
          break;
        }
      }
    }

    // If the job/chunk failed...
    if (chunkFailed) {
      // reset this chunk
      this.file.resetChunk(this.chunk);

      // Check if we want to autopause
      if (Config.upload.retry.autopause) {
        this.transfer.pause();
        this.shouldContinue(); // no, you should not.
      }

      // Or just fail completely
      throw 'JobFailed';
    }

    // Let's get what we need and then clear the buffer
    const uploadedBytes = this.buffer.size;
    this.buffer = null;

    // Do some byte math.
    this.file.state.bytesSent += uploadedBytes;
    this.file.state.bytesProgress[this.chunk] = 0;
    delete this.file.state.bytesProgress[this.chunk];

    // We're done
    this.state = 'finished';

    // Return our time measurements
    return {
      jobTime: Gauge.since(this.times.start),
      uploadTime: this.times.upload,
      uploadSpeed: uploadedBytes / this.times.upload,
    };
  }

  async validateId() {
    // If the file already has an ID, we're all set. Yeah!
    if (this.file.id) {
      return;
    }

    // Block this file for other jobs
    this.file.block();

    // Get the file entry id (block file in the meanwhile)
    try {
      this.file.id = await API.fetchFileObjectId(this.transfer.id, {
        name: this.file.name,
        size: this.file.size,
      });
    } catch (e) {
      if (e === 'RequestAborted' && this.state === 'stopped') {
        return;
      }
      throw e;
    }

    // Unblock the job again
    this.file.unblock();
  }

  validateFile() {
    if (this.file.validateSize()) {
      return true;
    }
    throw 'FileSizeChanged';
  }

  calculateOffset() {
    // Calculate chunk slice position and length
    this.offset = this.file.chunkSize * this.chunk;
    this.length = this.file.chunkSize;
    if (this.offset + this.length > this.file.size) {
      this.length = this.file.size - this.offset;
    }
  }

  readChunk() {
    // Create a slice from our file
    const sliceMethod =
      this.file.ref.slice ||
      this.file.ref.mozSlice ||
      this.file.ref.webkitSlice;
    this.buffer = sliceMethod.call(
      this.file.ref,
      this.offset,
      this.offset + this.length
    );
  }

  async putChunk(action) {
    this.cancelToken = CancelToken.source();
    this.file.state.bytesProgress[this.chunk] = 0;
    this.times.upload = Gauge.start();

    await request({
      method: 'put',
      path: action,
      params: this.buffer,
      cancelToken: this.cancelToken.token,
      onUploadProgress: (progressEvent) => {
        // Measure the bytes sent at this stage.
        this.file.state.bytesProgress[this.chunk] = Math.round(
          (progressEvent.loaded / progressEvent.total) * this.buffer.size
        );
      },
    });

    this.times.upload = Gauge.since(this.times.upload);
  }

  async requestForm() {
    // Fetch the formdata from our API
    try {
      return await API.fetchFileObjectForm(
        this.transfer.id,
        this.file.id,
        {
          chunk_number: this.chunk + 1, // offset by 1
          chunk_size: this.length,
          chunk_crc: this.crc,
          retries: this.attempts,
        },
        //retries
        {
          limit: 10,
        }
      );
    } catch (e) {
      if (e === 'RequestAborted' && this.state === 'stopped') {
        return;
      }
      throw e;
    }
  }

  async finalizeFile() {
    // If there are still chunks we can't finalize.
    if (this.file.hasRemainingChunks()) {
      return;
    }

    // Tell the API that we want to close the book on this one
    let info;
    try {
      info = await API.finalizeFileObject(this.transfer.id, this.file.id, {
        chunk_count: this.file.chunks,
      });
    } catch (e) {
      if (e === 'RequestAborted' && this.state === 'stopped') {
        return;
      }
      throw e;
    }

    // No missing chunks, yay!
    if (!info || !info['missing_chunks']) {
      return;
    }

    // Reset these chunks, we still need them.
    for (let n = info['missing_chunks'].length; n--; ) {
      this.file.resetChunk(parseInt(info['missing_chunks'][n], 10) - 1);
    }
  }

  sleep(interval) {
    // This looks so bad I know, but it kind of made sense at the time
    // for the retry loop.
    return new Promise((resolve) => setTimeout(resolve, interval));
  }
}
